import { __decorate } from "tslib";
import { Component, Inject, Prop, Vue } from 'vue-property-decorator';
import { BootstrapButton, BootstrapIcon, BootstrapImage, BootstrapLink } from '../../../../dsl/front';
import { isRelated } from '../../../../services';
import { prepareImage } from '../../../_filters/prepare-image';
import { PlayIcon } from '../../../_components/ui';
import VideoPlayer from '../../../_components/VideoPlayer/VideoPlayer';
/**
 * Component capable to render the `EmbedModule`
 * with the version set `EmbedVersion.Video`
 *
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
let EmbedVideoUi = class EmbedVideoUi extends Vue {
    constructor() {
        super(...arguments);
        /**
         * Determines whether the YouTube player has been initialised.
         */
        this.isPlayerInitialised = false;
        /**
         * Resolved video file
         */
        this.resolvedVideo = null;
        /**
         * Ready to use `Image` object, resolved using `RelatedService`.
         */
        this.resolvedThumbnail = null;
        this.videoAttrId = this.$id() + 'video';
    }
    get hasThumbnail() {
        return this.resolvedThumbnail !== null;
    }
    get thumbnailSrc() {
        if (!this.resolvedThumbnail) {
            return null;
        }
        return this.resolvedThumbnail.src;
    }
    get baseVideoMetadata() {
        return {
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: this.description ? this.description : this.parentTitle,
            thumbnailUrl: this.resolvedThumbnail ? this.resolvedThumbnail.src : '',
            uploadDate: this.publicationDate
        };
    }
    get videoFile() {
        return {
            file: this.resolvedVideo
        };
    }
    onPlayBtnClick() {
        this.eventBus.emit('ui:video-player.play', {
            playerId: this.videoAttrId,
            autoplay: true
        });
    }
    onVideoInit() {
        this.isPlayerInitialised = true;
    }
    /**
     * @inheritDoc
     */
    async fetchRelated() {
        await this.resolveFile();
        await this.resolveThumbnail();
    }
    /**
     * Resolves the pdf file, if any.
     */
    async resolveFile() {
        if (typeof this.video === 'undefined') {
            return;
        }
        this.resolvedVideo = this.relatedService.resolveSync(this.video);
    }
    /**
     * Resolves the thumbnail image, if any.
     */
    async resolveThumbnail() {
        if (typeof this.thumbnail === 'undefined' || !isRelated(this.thumbnail)) {
            return;
        }
        const imageFile = this.relatedService.resolveSync(this.thumbnail);
        if (!imageFile) {
            return;
        }
        const maxWidth = this.isMobile ? 768 : 800;
        this.resolvedThumbnail = prepareImage(imageFile, this.imageRatio, maxWidth, this.isLazy);
    }
};
__decorate([
    Inject({ from: 'eventBus' })
], EmbedVideoUi.prototype, "eventBus", void 0);
__decorate([
    Prop({
        type: String,
        required: false
    })
], EmbedVideoUi.prototype, "description", void 0);
__decorate([
    Prop({
        type: String,
        required: true
    })
], EmbedVideoUi.prototype, "imageRatio", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: 'cms'
    })
], EmbedVideoUi.prototype, "origin", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], EmbedVideoUi.prototype, "thumbnail", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], EmbedVideoUi.prototype, "video", void 0);
__decorate([
    Prop({
        type: String,
        required: false
    })
], EmbedVideoUi.prototype, "videoAdsTag", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], EmbedVideoUi.prototype, "isLazy", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], EmbedVideoUi.prototype, "isMobile", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: ''
    })
], EmbedVideoUi.prototype, "parentTitle", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: ''
    })
], EmbedVideoUi.prototype, "publicationDate", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], EmbedVideoUi.prototype, "relatedService", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], EmbedVideoUi.prototype, "windowService", void 0);
EmbedVideoUi = __decorate([
    Component({
        name: 'EmbedVideoUi',
        components: {
            BootstrapLink,
            BootstrapButton,
            BootstrapIcon,
            BootstrapImage,
            PlayIcon,
            VideoPlayer
        },
        async created() {
            await this.fetchRelated();
        },
        async mounted() {
            this.$emit('ready');
        },
        template: `
    <figure class="embed-video">
    <div class="embed-video__content-wrapper">
      <VideoPlayer class="embed-youtube__player" :video="videoFile" :videoAttrId="videoAttrId" forceType="hls"
                   :videoAdsTag="videoAdsTag"
                   :baseVideoMetadata="baseVideoMetadata" :eventBus="eventBus" :origin="origin" @init="onVideoInit"/>

      <BootstrapImage v-if="resolvedThumbnail" v-bind="resolvedThumbnail" class="embed-video__thumbnail"
                      :class="{ '--hidden': isPlayerInitialised }"/>

      <BootstrapButton @click="onPlayBtnClick" class="embed-video__play-btn"
                       :class="{ '--hidden': isPlayerInitialised }">
        <PlayIcon/>
      </BootstrapButton>
    </div>

    <figcaption v-if="description" class="embed-video__desc">{{ description }}</figcaption>
    </figure>
  `
    })
], EmbedVideoUi);
export { EmbedVideoUi };
export default EmbedVideoUi;
